import React, { useState, useRef, useEffect } from 'react';
import { Package } from 'lucide-react';

const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  width,
  height,
  blurHash = true,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  const hasSrc = src && src.trim() && !src.includes('placeholder');

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
    >
      {/* Placeholder for no/broken image */}
      {(!hasSrc || error) && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 flex flex-col items-center justify-center">
          <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-1" />
          <span className="text-[10px] text-gray-400 dark:text-gray-500">No image</span>
        </div>
      )}

      {/* Blur loading placeholder */}
      {hasSrc && !error && !loaded && blurHash && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800 animate-pulse" />
      )}

      {inView && hasSrc && !error && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          {...props}
        />
      )}
    </div>
  );
};

export default OptimizedImage;
